import React, { useMemo, useCallback } from 'react';
import { Project } from '../../../../types/project';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';

interface ProjectInfoCardProps {
  project: Project;
  tasksTotal: number;
  tasksVital: number;
  tasksUrgent: number;
  tasksCompleted: number;
  formatDate: (date: string) => string;
  onEditClick: () => void;
  onShare: () => void;
  onDelete: () => void;
}

/**
 * iOS 风格的项目信息卡片组件
 */
const ProjectInfoCardComponent: React.FC<ProjectInfoCardProps> = ({
  project,
  tasksTotal,
  tasksVital,
  tasksUrgent,
  tasksCompleted,
  formatDate,
  onEditClick,
  onShare,
  onDelete,
}) => {
  const { t } = useTranslation(['v3/projects', 'common']);

  // 计算完成率 - 使用 useMemo 优化
  const completionRate = useMemo(() =>
    tasksTotal > 0 ? Math.round((tasksCompleted / tasksTotal) * 100) : 0
  , [tasksTotal, tasksCompleted]);

  return (
    <motion.div
      initial={{ opacity: 0, y: -10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      className="mb-6 animate-ios-fade-in"
    >
      <div className="bg-white dark:bg-ios-dark-gray5 rounded-ios-lg shadow-ios dark:shadow-ios-dark overflow-hidden">
        {/* 项目标题和编辑按钮 */}
        <div className="px-5 pt-5 pb-3 flex justify-between items-start">
          <div className="flex-1 min-w-0">
            <h1 className="text-ios-title2 font-semibold text-ios-black dark:text-white mb-1 truncate">
              {project.title}
            </h1>
            <p className="text-ios-footnote text-ios-gray2 dark:text-ios-dark-gray2">
              {t('v3/projects:info.createdAt', { date: formatDate(project.createdAt) })}
            </p>
          </div>
          <div className="flex -mr-1">
            <button
              onClick={onShare}
              className="p-2 text-ios-green dark:text-ios-dark-green rounded-full hover:bg-ios-gray6/80 dark:hover:bg-ios-dark-gray4/80 active:bg-ios-gray6 dark:active:bg-ios-dark-gray4 transition-colors"
              aria-label={t('v3/projects:actions.shareProject')}
            >
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" className="w-5 h-5">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
              </svg>
            </button>
            <button
              onClick={onEditClick}
              className="ml-1 p-2 text-ios-blue dark:text-ios-dark-blue rounded-full hover:bg-ios-gray6/80 dark:hover:bg-ios-dark-gray4/80 active:bg-ios-gray6 dark:active:bg-ios-dark-gray4 transition-colors"
              aria-label={t('v3/projects:actions.editProject')}
            >
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" className="w-5 h-5">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
              </svg>
            </button>
            <button
              onClick={onDelete}
              className="ml-1 p-2 text-ios-red dark:text-ios-dark-red rounded-full hover:bg-ios-gray6/80 dark:hover:bg-ios-dark-gray4/80 active:bg-ios-gray6 dark:active:bg-ios-dark-gray4 transition-colors"
              aria-label={t('v3/projects:actions.deleteProject')}
            >
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" className="w-5 h-5">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
            </button>
          </div>
        </div>

        {/* 项目描述 */}
        {project.context && (
          <div className="px-5 pb-4">
            <p className="text-ios-body text-ios-black dark:text-white whitespace-pre-wrap">
              {project.context}
            </p>
          </div>
        )}

        {/* 项目统计信息 */}
        <div className="px-5 py-4 bg-ios-gray6/80 dark:bg-ios-dark-gray4/80 flex flex-wrap gap-2.5">
          {/* 总任务数 */}
          <div className="flex items-center bg-white/90 dark:bg-ios-dark-gray5/90 px-3.5 py-1.5 rounded-ios-full shadow-sm">
            <span className="text-ios-subhead font-medium text-ios-black dark:text-white">
              {t('v3/projects:stats.totalTasks', { count: tasksTotal })}
            </span>
          </div>

          {/* 完成率 */}
          <div className="flex items-center bg-white/90 dark:bg-ios-dark-gray5/90 px-3.5 py-1.5 rounded-ios-full shadow-sm">
            <span className="text-ios-subhead font-medium text-ios-green dark:text-ios-dark-green">
              {t('v3/projects:stats.completionRate', {
                completed: tasksCompleted,
                total: tasksTotal,
                rate: completionRate
              })}
            </span>
          </div>

          {/* 重要任务 */}
          {tasksVital > 0 && (
            <div className="flex items-center bg-white/90 dark:bg-ios-dark-gray5/90 px-3.5 py-1.5 rounded-ios-full shadow-sm">
              <span className="text-ios-subhead font-medium text-ios-orange dark:text-ios-dark-orange">
                {t('v3/projects:stats.vitalTasks', { count: tasksVital })}
              </span>
            </div>
          )}

          {/* 紧急任务 */}
          {tasksUrgent > 0 && (
            <div className="flex items-center bg-white/90 dark:bg-ios-dark-gray5/90 px-3.5 py-1.5 rounded-ios-full shadow-sm">
              <span className="text-ios-subhead font-medium text-ios-red dark:text-ios-dark-red">
                {t('v3/projects:stats.urgentTasks', { count: tasksUrgent })}
              </span>
            </div>
          )}
        </div>
      </div>
    </motion.div>
  );
};

// 使用 React.memo 包装组件，避免不必要的重新渲染
export const ProjectInfoCard = React.memo(ProjectInfoCardComponent, (prevProps, nextProps) => {
  // 自定义比较函数，只有在关键属性变化时才重新渲染
  return (
    prevProps.project.id === nextProps.project.id &&
    prevProps.project.title === nextProps.project.title &&
    prevProps.project.context === nextProps.project.context &&
    prevProps.tasksTotal === nextProps.tasksTotal &&
    prevProps.tasksVital === nextProps.tasksVital &&
    prevProps.tasksUrgent === nextProps.tasksUrgent &&
    prevProps.tasksCompleted === nextProps.tasksCompleted
  );
});
